import React from 'react';

import {
  FDContentContainer, FDBox, FDStack, FDSecondaryActionIconButton, FDIcon,
} from '@facet-wealth/design';
import { WizardProgressBar } from 'Views/Page/Content/Templates/WizardTemplates/RoadmapDeliverable/Wrapper/WizardProgressBar';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { TaxDashboardWizardFooterNav } from 'Views/Page/Content/Modal/Layouts/TaxDashboardWizard/Components/TaxDashboardWizardFooterNav';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { navigateByRelationship } from 'Views/Page/Content/Templates';
import fr from 'facet-rest';

export const TaxDashboardWizardNav = ({
  children,
  extraNavRelationshipName,
  extraNavLabel,
  onClickExtraNavOverride,
  disabled = false,
  hideProgress = false,
  customButtons = false,
  onNextOverrideHref,
}) => {
  const { resource, onSubmit, saving } = useMetaLayoutContext();
  const { loadPageContentLink, refreshPageContentCollection } = usePageContentContext();
  const { clearModalCollection, loadModalCollectionByLink } = useModalContext();

  const handlePrevious = resource.withRelationship(fr.relationships.previousLink.name,
    (rel) => async () => loadModalCollectionByLink(rel.link('related').url()),
    () => null);

  const hasGoToRelationship = resource.relationship(fr.relationships.goto.name);

  // The primary cta submits the step. The goto relationship will override the primary CTA if present.
  const handleNext = resource.withRelationship(
    fr.relationships.goto.name,
    (rel) => async () => {
      return navigateByRelationship(rel, loadModalCollectionByLink, loadPageContentLink, clearModalCollection);
    },
    () => onSubmit,
  );

  const getClickableTextLabel = () => {
    if (extraNavLabel) {
      return extraNavLabel;
    }

    if (extraNavRelationshipName) {
      return resource.withRelationship(
        extraNavRelationshipName,
        (rel) => {
          return rel.meta('prompt');
        },
        () => null,
      );
    }

    return null;
  };

  const onClickText = async () => {
    if (extraNavRelationshipName) {
      return resource.withRelationship(
        extraNavRelationshipName,
        (rel) => {
          return navigateByRelationship(rel, loadModalCollectionByLink, loadPageContentLink, clearModalCollection);
        },
        () => null,
      );
    }

    if (onClickExtraNavOverride) {
      return onClickExtraNavOverride();
    }

    return null;
  };

  const onClose = async () => {
    await clearModalCollection();
    await refreshPageContentCollection();
  };

  return (
    <FDStack paddingY={0}>
      <FDContentContainer>
        {/* Nav with Progress */}
        <FDBox>
          <FDStack alignItems={'center'} position={'relative'} paddingY={3}>
            <FDSecondaryActionIconButton size={'lg'} icon={'close'} disablePadding onClick={onClose} iconColor={'textPrimary'} />
            <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
              <FDIcon size={'md'} icon={'fac'} />
            </div>
          </FDStack>
        </FDBox>
        {!hideProgress && (
        <FDStack>
          <WizardProgressBar progress={resource.attr('percentComplete')} />
        </FDStack>
        )}

        {children}

        {/* Footer with continue and go back */}
        {!customButtons && (
          <FDStack direction={'column'} spacing={2} paddingY={4}>
            <TaxDashboardWizardFooterNav
              disabled={disabled || (!hasGoToRelationship && !resource.meta().canPost())}
              loading={saving}
              onNext={handleNext}
              onNextHref={onNextOverrideHref}
              onPrevious={handlePrevious}
              primaryActionLabel={resource.attr('primaryActionLabel') || 'Continue'}
              secondaryActionLabel={resource.attr('secondaryActionLabel') || 'Go Back'}
              clickableTextLabel={getClickableTextLabel()}
              onClickText={onClickText}
            />
          </FDStack>
        )}
      </FDContentContainer>
    </FDStack>
  );
};
